const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'API request failed');
  }
  return data;
};

export const appAPI = {
  getAllApps: async () => {
    const response = await fetch(`${API_BASE_URL}/apps`);
    return handleResponse(response);
  },

  getAppById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/apps/${id}`);
    return handleResponse(response);
  },

  getAppByPackage: async (packageName) => {
    const response = await fetch(`${API_BASE_URL}/apps/package/${packageName}`);
    return handleResponse(response);
  },

  updateApp: async (token, appId, updates) => {
    const response = await fetch(`${API_BASE_URL}/apps/${appId}/complete`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });
    return handleResponse(response);
  },

  deleteApp: async (token, appId) => {
    const response = await fetch(`${API_BASE_URL}/apps/${appId}/complete`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return handleResponse(response);
  },

  approveApp: async (token, appId) => {
    const response = await fetch(`${API_BASE_URL}/apps/${appId}/approve`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return handleResponse(response);
  },

  rejectApp: async (token, appId) => {
    const response = await fetch(`${API_BASE_URL}/apps/${appId}/reject`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return handleResponse(response);
  }
};

export const downloadAPI = {
  recordDownload: async (appId) => {
    const response = await fetch(`${API_BASE_URL}/downloads/record`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ appId })
    });
    return handleResponse(response);
  },

  getDownloadStats: async (appId) => {
    const response = await fetch(`${API_BASE_URL}/downloads/stats/${appId}`);
    return handleResponse(response);
  }
};

export const ratingAPI = {
  addRating: async (appId, rating, review, userName) => {
    const response = await fetch(`${API_BASE_URL}/ratings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ appId, rating, review, userName })
    });
    return handleResponse(response);
  },

  getRatings: async (appId, page = 1) => {
    const response = await fetch(`${API_BASE_URL}/ratings/${appId}?page=${page}`);
    return handleResponse(response);
  },

  replyToRating: async (token, ratingId, reply) => {
    const response = await fetch(`${API_BASE_URL}/ratings/${ratingId}/reply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ reply })
    });
    return handleResponse(response);
  },

  deleteRating: async (token, ratingId) => {
    const response = await fetch(`${API_BASE_URL}/ratings/${ratingId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return handleResponse(response);
  }
};

export const statsAPI = {
  getOverview: async () => {
    const response = await fetch(`${API_BASE_URL}/stats/overview`);
    return handleResponse(response);
  },

  getDashboard: async () => {
    const response = await fetch(`${API_BASE_URL}/stats/dashboard`);
    return handleResponse(response);
  }
};

export const shareAPI = {
  createShareLink: async (appId, title, description, expiresInDays) => {
    const response = await fetch(`${API_BASE_URL}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ appId, title, description, expiresInDays })
    });
    return handleResponse(response);
  },

  getShareLinkByToken: async (token) => {
    const response = await fetch(`${API_BASE_URL}/share/${token}`);
    return handleResponse(response);
  },

  getShareLinkStats: async (token) => {
    const response = await fetch(`${API_BASE_URL}/share/${token}/stats`);
    return handleResponse(response);
  },

  getAllShareLinks: async (appId) => {
    const url = appId ? `${API_BASE_URL}/share/all?appId=${appId}` : `${API_BASE_URL}/share/all`;
    const response = await fetch(url);
    return handleResponse(response);
  }
};

export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password })
    });
    return handleResponse(response);
  },

  verifyToken: async (token) => {
    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return handleResponse(response);
  },

  changePassword: async (token, currentPassword, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });
    return handleResponse(response);
  }
};

export const settingsAPI = {
  getAllSettings: async (publicOnly = false) => {
    const url = publicOnly ? `${API_BASE_URL}/settings?publicOnly=true` : `${API_BASE_URL}/settings`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  getSetting: async (key) => {
    const response = await fetch(`${API_BASE_URL}/settings/${key}`);
    return handleResponse(response);
  },

  updateSetting: async (token, key, value, description, isPublic) => {
    const response = await fetch(`${API_BASE_URL}/settings/${key}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ value, description, isPublic })
    });
    return handleResponse(response);
  },

  createSetting: async (token, key, value, type, category, description, isPublic) => {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ key, value, type, category, description, isPublic })
    });
    return handleResponse(response);
  },

  deleteSetting: async (token, key) => {
    const response = await fetch(`${API_BASE_URL}/settings/${key}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return handleResponse(response);
  }
};

export const contactAPI = {
  submitForm: async (data) => {
    const response = await fetch(`${API_BASE_URL}/contact/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getAllMessages: async () => {
    const response = await fetch(`${API_BASE_URL}/contact/messages`);
    return handleResponse(response);
  },

  getUnreadCount: async () => {
    const response = await fetch(`${API_BASE_URL}/contact/unread-count`);
    return handleResponse(response);
  },

  markAsRead: async (id) => {
    const response = await fetch(`${API_BASE_URL}/contact/${id}/read`, {
      method: 'PATCH'
    });
    return handleResponse(response);
  },

  replyToMessage: async (id, reply) => {
    const response = await fetch(`${API_BASE_URL}/contact/${id}/reply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reply })
    });
    return handleResponse(response);
  },

  deleteMessage: async (id) => {
    const response = await fetch(`${API_BASE_URL}/contact/${id}`, {
      method: 'DELETE'
    });
    return handleResponse(response);
  }
};
